import { signOut as amplifySignOut } from "aws-amplify/auth";
import _ from "lodash";
import {
  identify,
  Identify,
  reset,
  setUserId,
} from "@amplitude/analytics-browser";

import { authLoaded, signOut } from "../../reducers/auth";
import { Flags } from "../../reducers/models/flags";
import { UserGroup } from "../group";
import { getAccessToken } from "../accessToken";
import { useAppDispatch } from "./store";
import { useCallback } from "react";
import useCheckAccountCreated from "./users/accountCreation";
import { errorToString, tracer, useSendError } from "../observability/logging";
import { Span } from "@opentelemetry/api";
import {
  NOT_AUTHORIZED_EXCEPTION,
  USER_UNAUTHENTICATED_EXCEPTION,
} from "../../constants/errors";

const ACCEPTABLE_AUTH_EXCEPTIONS = new Set([
  USER_UNAUTHENTICATED_EXCEPTION,
  NOT_AUTHORIZED_EXCEPTION,
]);

export function useLoadAuth() {
  const dispatch = useAppDispatch();
  const checkAccountCreated = useCheckAccountCreated();
  const sendError = useSendError();

  return useCallback(async () => {
    return tracer.startActiveSpan("loadAuth", async (span: Span) => {
      try {
        const userAttributes = await checkAccountCreated();
        const accessToken = await getAccessToken();
        const groups = _.get(
          accessToken,
          "payload.cognito:groups",
          [],
        ) as UserGroup[];
        const userId = userAttributes["custom:flashpcb_user_id"];
        // Rely on cognito's email because there is a slim chance ours is stale if the
        // user quit the update account flow or an error occurred.
        const signedIn = Boolean(userAttributes);
        span.setAttributes({ groups, userId, signedIn });
        const email = userAttributes.email;
        if (userId === undefined || email === undefined) {
          throw Error("Invalid auth behavior");
        }
        dispatch(
          authLoaded({
            signedIn: signedIn,
            email: email,
            sub: userAttributes.sub as string,
            userId: userId,
            groups,
            flags: JSON.parse(userAttributes["custom:flags"] || "{}") as Flags,
          }),
        );
        // Amplitude stuff
        setUserId(userId);
        const identifyObj = new Identify();
        identifyObj.set("email", email);
        identify(identifyObj);
      } catch (error) {
        span.setAttribute("loadAuthError", errorToString(error));
        const userIsNotAuthenticated =
          error instanceof Error && ACCEPTABLE_AUTH_EXCEPTIONS.has(error.name);
        if (!userIsNotAuthenticated) {
          sendError(error, { activeSpan: span });
        }
        reset();
        dispatch(signOut());
        await amplifySignOut();
      } finally {
        span.end();
      }
    });
  }, [checkAccountCreated, dispatch, sendError]);
}
